// ADENDA z65 (Diego, 30-oct-2026) · bloque C: «Separar en otro sitio». Con recibos que la lectura marcó «Otro número de
// servicio» (otro lugar del mismo cliente: otra sucursal o la casa del dueño), se pasan a un sitio NUEVO del mismo proyecto con su
// tipo y su tarifa, sin empezar de nuevo. Los candados viven en el servidor (dominio/sitios-proyecto.ts); aquí el candado 1:
// el tipo y la tarifa que guardó el servidor son los que se pidieron, o se dice.
import { useState } from 'react';
import { Split } from 'lucide-react';
import type { Cotizacion, Cuestionario, Tarifa } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { navegar } from '../../ruteo.tsx';
import { Boton, CampoSelect, CampoTexto, ErrorEnLinea } from '../../componentes/ui.tsx';
import { formaDesde } from '../../logica/cuestionario.ts';
import { ETIQUETA_TIPO_SITIO, TIPOS_SITIO, cuestionarioDeSitio, diferenciaSitio, igualAlPrincipal, opcionesTarifa, recibosDeOtroServicio, separarAplica } from '../../logica/sitios.ts';
import type { TipoSitio } from '../../logica/sitios.ts';
import { usarCotizacion } from './contexto.tsx';

export function SepararSitio({ cot, editable }: { cot: Cotizacion; editable: boolean }) {
  const { recargar } = usarCotizacion();
  const [abierto, setAbierto] = useState(false);
  const [actual, setActual] = useState(cot.multisitio?.sitio ?? '');
  const [nuevo, setNuevo] = useState('');
  const [tipo, setTipo] = useState<TipoSitio | null>(null);
  const [tarifa, setTarifa] = useState<Tarifa | 'no_se'>('no_se');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ids = recibosDeOtroServicio(cot);
  if (!editable || !ids.length || !separarAplica(cot.borrador.cuestionario)) return null;

  async function separar() {
    setError(null);
    if (!cot.multisitio && actual.trim().length < 2) return setError(new Error('Escribe el nombre de este sitio (2 a 40 letras), p. ej. «Taller».'));
    if (nuevo.trim().length < 2) return setError(new Error('Escribe el nombre del sitio nuevo (2 a 40 letras), p. ej. «Casa del dueño».'));
    const forma = formaDesde(cot.borrador.cuestionario);
    const sitio = { nombre: nuevo.trim(), tipo, tarifa: tipo ? tarifa : null };
    let cuestionario: Cuestionario;
    if (igualAlPrincipal(forma, sitio)) cuestionario = cot.borrador.cuestionario;
    else {
      const r = cuestionarioDeSitio(forma, sitio);
      if ('error' in r) return setError(new Error(`«${sitio.nombre}» ${r.error}`));
      cuestionario = r.cuestionario;
    }
    setEnviando(true);
    try {
      const r = await api.separarSitio(cot.id, { revision: cot.revision, sitio_actual: (cot.multisitio?.sitio ?? actual).trim(), sitio_nuevo: sitio.nombre, cuestionario, archivo_ids: ids });
      const d = diferenciaSitio(cuestionario, r.nueva.borrador.cuestionario);
      if (d) {
        await recargar();
        return setError(new Error(`Se separó, pero revisa el tipo y la tarifa de «${sitio.nombre}» (${r.nueva.folio}): ${d}. Ábrelo en «Sitios del proyecto» → «Editar».`));
      }
      navegar(`/cotizaciones/${r.nueva.id}/recibos`);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="franja-solo-ver separar-sitio" role="note" aria-label="Recibos de otro número de servicio">
      <Split aria-hidden />
      <div className="pila">
        <p>
          {ids.length === 1 ? 'Un recibo es' : `${ids.length} recibos son`} de otro número de servicio y no se usaron. Si son de otro lugar del mismo cliente (otra sucursal o la casa del dueño),
          sepáralos en otro sitio: esta cotización no cambia de cifras.
        </p>
        {!abierto ? (
          <div>
            <Boton chico variante="secundario" onClick={() => setAbierto(true)}>
              Separar en otro sitio
            </Boton>
          </div>
        ) : (
          <div className="pila">
            {!cot.multisitio && <CampoTexto etiqueta="Nombre de este sitio" placeholder="P. ej. Taller" maxLength={40} value={actual} onChange={(e) => setActual(e.target.value)} />}
            <CampoTexto etiqueta="Nombre del sitio nuevo" placeholder="P. ej. Casa del dueño" maxLength={40} value={nuevo} onChange={(e) => setNuevo(e.target.value)} />
            <div className="rejilla-2">
              <CampoSelect
                etiqueta="Tipo del sitio nuevo"
                value={tipo ?? ''}
                opciones={[{ valor: '', texto: 'Igual que este sitio' }, ...TIPOS_SITIO.map((t) => ({ valor: t, texto: ETIQUETA_TIPO_SITIO[t] }))]}
                onChange={(e) => {
                  setTipo((e.target.value || null) as TipoSitio | null);
                  setTarifa('no_se');
                }}
              />
              {tipo && <CampoSelect etiqueta="Tarifa del sitio nuevo" value={tarifa} opciones={opcionesTarifa(tipo, tarifa)} onChange={(e) => setTarifa(e.target.value as Tarifa | 'no_se')} />}
            </div>
            <div>
              <Boton chico variante="principal" cargando={enviando} onClick={() => void separar()}>
                Separar y abrir el sitio nuevo
              </Boton>
            </div>
          </div>
        )}
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
    </div>
  );
}
